import { Highlighter, Sparkles } from 'lucide-react'
import { useMemo } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useUISettingsStore } from '@/store/useUISettingsStore'

import { ALL_SECTIONS, findGlossaryEntry } from './book-ai-position'
import { glossaryKindConfig } from './glossary-kinds'
import type { BookAiReaderState } from './useBookAi'

type Props = {
  /** 選択中の文字 */
  text: string
  bookAi: BookAiReaderState
  onHighlight: () => void
  onAskAi: () => void
}

/**
 * 本文で文字を選択したときの操作（EPUB・PDF 共通）
 *
 * 選択しただけではシートを開かず、ボタンを押したときだけ動くので、誤操作で読書が止まらない。
 * 選んだ語が人物・用語集にあれば、AI を呼ばずにその説明をすぐ出す。
 */
export const SelectionActionBar = ({ text, bookAi, onHighlight, onAskAi }: Props) => {
  // ネタバレ防止中は今読んでいる章の分まで（自分で選んだ語なので今の章の説明も役に立つ）、切ってあれば本全体
  const spoilerGuard = useUISettingsStore((state) => state.glossarySpoilerGuard)
  const uptoIndex = spoilerGuard ? Math.max(bookAi.currentSectionIndex, 0) : ALL_SECTIONS
  const entry = useMemo(() => findGlossaryEntry(bookAi.sections, uptoIndex, text), [bookAi.sections, uptoIndex, text])
  const kind = entry ? glossaryKindConfig(entry.kind) : null
  const description = entry?.notes.at(-1)?.description

  return (
    <div
      className="fixed bottom-20 left-1/2 z-1000 flex w-[min(92vw,420px)] -translate-x-1/2 flex-col items-center gap-2"
      // 本文のタップ（メニューの表示切り替え）として扱われないようにする
      onClick={(e) => e.stopPropagation()}
    >
      {entry && kind && description && (
        <div className="w-full rounded-xl border bg-background/95 p-3 shadow-lg backdrop-blur-sm">
          <p className="flex items-center gap-1.5 text-sm font-semibold">
            <span className={cn('flex size-5 items-center justify-center rounded-full', kind.className)}>
              <kind.icon className="size-3" />
            </span>
            {entry.name}
            <span className="text-xs font-normal text-muted-foreground">{kind.label}</span>
          </p>
          <p className="mt-1 text-sm leading-relaxed wrap-break-word">{description}</p>
        </div>
      )}
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={onHighlight}
          className="rounded-full bg-yellow-400/90 text-black shadow-lg backdrop-blur-sm hover:bg-yellow-500/90"
        >
          <Highlighter className="size-4" />
          ハイライト
        </Button>
        {bookAi.status?.chat_ready && (
          <Button size="sm" onClick={onAskAi} className="rounded-full shadow-lg">
            <Sparkles className="size-4" />
            AIに質問
          </Button>
        )}
      </div>
    </div>
  )
}
